import { useEffect, useRef } from "react";

// Cloudflare Turnstile (gratuit). N'est monté que si REACT_APP_TURNSTILE_SITE_KEY
// est défini au build : sans clé, le formulaire fonctionne comme avant.
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;
const loadScript = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("no_window"));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => {
        scriptPromise = null;
        reject(new Error("turnstile_load_failed"));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
};

/**
 * Props :
 *  - siteKey      clé publique du widget
 *  - onToken(t)   appelé avec le jeton, ou "" quand il expire / échoue
 *  - resetSignal  incrémenter pour redemander un jeton (après un envoi refusé)
 */
export default function TurnstileWidget({ siteKey, onToken, resetSignal = 0 }) {
  const boxRef = useRef(null);
  const widgetId = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    let cancelled = false;
    loadScript()
      .then((ts) => {
        if (cancelled || !boxRef.current || widgetId.current !== null) return;
        widgetId.current = ts.render(boxRef.current, {
          sitekey: siteKey,
          theme: "dark",
          language: "fr",
          callback: (t) => onTokenRef.current(t),
          "expired-callback": () => onTokenRef.current(""),
          "error-callback": () => onTokenRef.current(""),
        });
      })
      .catch(() => onTokenRef.current(""));
    return () => {
      cancelled = true;
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* noop */ }
      }
      widgetId.current = null;
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetSignal > 0 && widgetId.current !== null && window.turnstile) {
      onTokenRef.current("");
      try { window.turnstile.reset(widgetId.current); } catch { /* noop */ }
    }
  }, [resetSignal]);

  return <div ref={boxRef} data-testid="contact-turnstile" className="min-h-[65px]" />;
}
